import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { artikel, kategorien } from "@/data/magazin";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";
import { SEO } from "@/components/SEO";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });
}

function formatDate(dateStr, lang) {
  return new Date(dateStr).toLocaleDateString(lang === 'it' ? 'it-IT' : 'de-DE', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
}

export default function Magazin() {
  const [activeKat, setActiveKat] = useState("Alle");
  const { lang } = useLanguage();
  useReveal();

  const filtered = activeKat === "Alle"
    ? artikel
    : artikel.filter(a => a.kategorie === activeKat);

  const getTitle = (a) => lang === 'it' && a.titel_it ? a.titel_it : a.titel;
  const getPreview = (a) => lang === 'it' && a.vorschau_it ? a.vorschau_it : a.vorschau;

  const katLabelsIT = {
    'Alle': 'Tutti',
    'Aufstockung': 'Sopraelevazione',
    'Dachstuhl': 'Capriate',
    'Holzfassaden': 'Facciate',
    'Sanierung': 'Risanamento',
    'Hotelbau': 'Alberghiero',
    'Planung': 'Pianificazione',
    'Holzhaus': 'Casa in legno',
    'Terrassen': 'Terrazze',
    'Nachhaltigkeit': 'Sostenibilità'
  };

  const getKatLabel = (k) => lang === 'it' ? (katLabelsIT[k] || k) : k;

  return (
    <div className="pt-20 md:pt-24">
      <SEO
        title="Magazin – Holzbau-Wissen aus Südtirol"
        description="Fachartikel zu Dachstuhl, Aufstockung, Holzfassaden, Baugenehmigung und KlimaHaus. Wissen aus der Praxis von Holzbuddy – Zimmerei Meran, Südtirol."
        canonical="/magazin"
      />
      {/* Header */}
      <section className="section-gap bg-hb-off-white border-b border-hb-border">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-6 reveal">
            <T de="Holz-Wissen" it="Conoscenza del legno" />
          </span>
          <h1 className="section-title max-w-2xl mb-6 reveal reveal-delay-1" data-testid="magazin-heading">
            <T de="Magazin." it="Rivista." />
          </h1>
          <p className="section-subtitle max-w-xl reveal reveal-delay-2">
            <T de="Wissen aus der Praxis. Für Bauherren, Planer und alle, die Holzbau besser verstehen wollen."
               it="Conoscenza dalla pratica. Per committenti, progettisti e per tutti coloro che vogliono capire meglio la costruzione in legno." />
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-6 bg-white border-b border-hb-border sticky top-16 md:top-20 z-30">
        <div className="container-hb">
          <div className="flex items-center gap-3 overflow-x-auto">
            {kategorien.map((kat) => (
              <button
                key={kat}
                data-testid={`magazin-filter-${kat}`}
                onClick={() => setActiveKat(kat)}
                className={`flex-shrink-0 px-5 py-2 text-xs tracking-widest uppercase font-semibold transition-all ${
                  activeKat === kat
                    ? 'bg-hb-dark text-white'
                    : 'bg-transparent border border-hb-border text-hb-text hover:border-hb-dark'
                }`}
              >
                {kat === 'Alle' ? <T de="Alle" it="Tutti" /> : getKatLabel(kat)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="section-gap bg-white" data-testid="artikel-grid">
        <div className="container-hb">
          {/* Featured first */}
          {filtered.length > 0 && (
            <Link
              to={`/magazin/${filtered[0].slug}`}
              data-testid={`artikel-featured-${filtered[0].id}`}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-px bg-hb-border card-hover mb-px reveal"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={filtered[0].bild}
                  alt={getTitle(filtered[0])}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="bg-white p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="label-text text-hb-wood">{filtered[0].kategorie}</span>
                  <span className="text-hb-muted text-xs">{filtered[0].lesezeit}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-manrope font-bold text-hb-dark mb-4 group-hover:text-hb-wood transition-colors leading-tight">
                  {getTitle(filtered[0])}
                </h2>
                <p className="text-sm text-hb-text font-inter leading-relaxed mb-6">
                  {getPreview(filtered[0])}
                </p>
                <div className="flex items-center gap-2 text-xs tracking-widest uppercase font-semibold text-hb-dark group-hover:text-hb-wood transition-colors">
                  <T de="Lesen" it="Leggi" /> <span className="group-hover:translate-x-1 transition-transform inline-block">→</span>
                </div>
                <p className="text-xs text-hb-muted mt-4 font-inter">{formatDate(filtered[0].datum, lang)}</p>
              </div>
            </Link>
          )}

          {/* Rest grid */}
          {filtered.length > 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-hb-border">
              {filtered.slice(1).map((a, i) => (
                <Link
                  key={a.id}
                  to={`/magazin/${a.slug}`}
                  data-testid={`artikel-card-${a.id}`}
                  className={`group bg-white card-hover reveal reveal-delay-${(i % 3) + 1}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={a.bild}
                      alt={getTitle(a)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-[10px] tracking-widest uppercase text-hb-wood font-semibold">{a.kategorie}</span>
                      <span className="text-[10px] text-hb-muted">{a.lesezeit}</span>
                    </div>
                    <h3 className="font-manrope font-bold text-hb-dark mb-2 group-hover:text-hb-wood transition-colors leading-tight">
                      {getTitle(a)}
                    </h3>
                    <p className="text-sm text-hb-text font-inter">{getPreview(a)}</p>
                    <p className="text-xs text-hb-muted mt-3 font-inter">{formatDate(a.datum, lang)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {filtered.length === 0 && (
            <p className="text-center text-hb-muted py-20">
              <T de="Keine Artikel in dieser Kategorie." it="Nessun articolo in questa categoria." />
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
